// View-model types for the treasury withdraw-requests module.
// Produced by ../api/withdrawRequestsParser.ts. Consumers (containers,
// components, composables) bind to these — never to the wire DTOs in
// ../api/withdrawRequestsContracts.ts.

export enum WITHDRAW_REQUEST_STATUS {
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  AWAITING_SETTLEMENT = 'awaiting_settlement',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
  FAILED = 'failed',
  CANCELED = 'canceled',
}

export type WithdrawRequestStatus = `${WITHDRAW_REQUEST_STATUS}`

// ─── Row (list response item) ────────────────────────────────────────────────

export interface WithdrawRequestRow {
  id: string
  investorId: string
  fiatWalletId: string
  bankAccountId: string
  status: WithdrawRequestStatus
  amount: string
  currencyCode: string
  externalReferenceId: string | null
  rejectionReason: string | null
  approvedAt: string | null
  completedAt: string | null
  createdAt: string
  investorName: string
  investorEmail: string
}

export interface WithdrawRequestsListResponse {
  data: WithdrawRequestRow[]
  meta: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

// ─── Status counts ───────────────────────────────────────────────────────────

export interface WithdrawRequestStatusCounts {
  pendingApproval: number
  awaitingSettlement: number
  completed: number
  rejected: number
}

// ─── Detail ──────────────────────────────────────────────────────────────────

export interface WithdrawRequestInvestor {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  kycStatus: string | null
}

export interface WithdrawRequestInvestorBankAccount {
  id: string
  bankName: string
  accountHolderName: string
  iban: string | null
  accountNumberMasked: string
}

export interface WithdrawRequestTreasuryAccount {
  id: string
  accountName: string
  bankName: string
  iban: string | null
  currencyCode: string
}

export interface WithdrawRequestSettlement {
  settlementReference: string | null
  settledAt: string | null
}

export interface WithdrawRequestAudit {
  approvedBy: string | null
  approvedAt: string | null
}

export interface WithdrawRequestDetail {
  id: string
  status: WithdrawRequestStatus
  amount: string
  currencyCode: string
  externalReferenceId: string | null
  rejectionReason: string | null
  createdAt: string
  investor: WithdrawRequestInvestor
  investorBankAccount: WithdrawRequestInvestorBankAccount
  treasuryBankAccount: WithdrawRequestTreasuryAccount | null
  settlement: WithdrawRequestSettlement
  audit: WithdrawRequestAudit
}

// ─── Request params / inputs ─────────────────────────────────────────────────

export interface GetWithdrawRequestsParams {
  page?: number
  limit?: number
  status?: WithdrawRequestStatus
  fromDate?: string
  toDate?: string
  investorId?: string
  investorName?: string
  investorEmail?: string
}

export interface ApproveWithdrawRequestInput {
  id: string
  treasuryBankAccountId: string
  idempotencyKey: string
}

export interface MarkSettledWithdrawRequestInput {
  id: string
  settlementReference: string
  idempotencyKey: string
}

export interface RejectWithdrawRequestInput {
  id: string
  reason: string
  idempotencyKey: string
}
